import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface Usuario {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface SueldoConfig {
  operadorId: number;
  mes: number;
  anio: number;
  sueldoReferencia: number;
  valorDiaPago: number;
  tipoPago: 'objetivos' | 'horas'; 
  valorHora?: number; 
  plusTipo: 'porcentaje' | 'fijo';
  plusPorcentaje: number;
  plusMontoFijo: number;
  objetivoAuditorias?: number;
  objetivoCorrecciones?: number;
  cobraFrancos: boolean;
  esSueldoFijo: boolean;
  adminGanaSueldo: boolean;
  fechaIngreso?: string;
  diasVacaciones: number;
  francosMarcados?: number[];
  pagoDobleEnFrancos: boolean; // Si se paga doble cuando trabaja en días franco/feriado
  valorDiaTrabajoEnFranco?: number; // Valor específico cuando trabaja en franco (opcional)
}

interface SueldoData {
  sueldo: {
    sueldoReferencia: number;
    sueldoFinal: number;
    porcentajeObjetivo: number;
    estado: string;
    mes: number;
    anio: number;
    auditoriasHechas: number;
    correccionesHechas: number;
    objetivoAuditorias?: number;
    objetivoCorrecciones?: number;
  };
  mensaje: string;
  detalle: {
    sueldoReferencia: number;
    diasPagos: {
      total: number;
      monto: number;
    };
    objetivo: {
      monto: number;
      porcentaje: number;
      montoGanado: number;
    };
    auditorias: {
      hechas: number;
      monto: number;
    };
    plus: {
      tipo: string;
      porcentaje: number;
      montoFijo: number;
      monto: number;
    };
    total: number;
  };
}

interface WorkSessionData {
  sesionActiva: boolean;
  sessionId: string | null;
  loginTime: string | null;
  horasActuales: number;
  horasRegulares: number;
  horasExtra: number;
  necesitaAprobacion: boolean;
  estado: string;
  alertaEnviada: boolean;
  horasExtraAprobadas: boolean;
  message: string;
}

interface SueldosConfigTableProps {
  user: any;
  onConfigUpdate: () => void;
  selectedUserForEdit?: any;
  onUserEditComplete?: () => void;
}

const SueldosConfigTable: React.FC<SueldosConfigTableProps> = ({ user, onConfigUpdate, selectedUserForEdit, onUserEditComplete }) => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [sueldoData, setSueldoData] = useState<SueldoData | null>(null);
  const [workSessionData, setWorkSessionData] = useState<WorkSessionData | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [config, setConfig] = useState<SueldoConfig>({
    operadorId: 0,
    mes: new Date().getMonth() + 1,
    anio: new Date().getFullYear(),
    sueldoReferencia: 25000,
    valorDiaPago: 50,
    tipoPago: 'objetivos',
    valorHora: 0,
    plusTipo: 'porcentaje',
    plusPorcentaje: 0,
    plusMontoFijo: 0,
    cobraFrancos: true,
    esSueldoFijo: false,
    adminGanaSueldo: false,
    diasVacaciones: 0,
    francosMarcados: [],
    pagoDobleEnFrancos: false,
    valorDiaTrabajoEnFranco: 0
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Estado para comisiones de ejecutivos
  const [comisionConfig, setComisionConfig] = useState({
    comision_captacion: '',
    comision_cobro: '',
    tipo_comision_captacion: 'porcentaje',
    tipo_comision_cobro: 'porcentaje',
    sueldo_base: ''  // Sueldo fijo mensual además de comisiones
  });

  useEffect(() => {
    loadUsuarios();

    // SI VIENE selectedUserForEdit (desde el botón Editar), auto-seleccionar inmediatamente
    setTimeout(() => {
      if (selectedUserForEdit && selectedUserForEdit.operador) {
        handleUserSelect(selectedUserForEdit.operador);
      }
    }, 100);
  }, []);

  // Abrir automáticamente el usuario seleccionado desde el botón editar
  useEffect(() => {
    if (selectedUserForEdit && selectedUserForEdit.operador && usuarios.length > 0) {
      const usuario = usuarios.find(u => u.id === selectedUserForEdit.operadorId);
      if (usuario) {
        setSelectedUser(usuario);
        handleUserSelect(usuario);
      }
    }
  }, [selectedUserForEdit, usuarios]);

  // Cuando el modal se abre para editar, auto-seleccionar el usuario correspondiente
  useEffect(() => {
    if (selectedUserForEdit && selectedUserForEdit.operador && usuarios.length > 0 && !selectedUser) {
      const usuario = usuarios.find(u => u.id === selectedUserForEdit.operadorId);
      if (usuario) {
        handleUserSelect(usuario);
      }
    }
  }, [selectedUserForEdit, usuarios, selectedUser]);

  const loadUsuarios = async () => {
    try {
      const response = await api.get('/users');
      
      let empleados = [];
      if (response.data.users) {
        empleados = response.data.users;
      } else if (response.data.data) {
        empleados = response.data.data;
      } else if (Array.isArray(response.data)) {
        empleados = response.data;
      } else {
        empleados = response.data;
      }
      
      const empleadosFiltrados = empleados.filter(u =>
        ['operador', 'supervisor', 'admin', 'contable', 'ejecutivo'].includes(u.role)
      );
      
      setUsuarios(empleadosFiltrados);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadUserDetails = async (usuario: Usuario) => {
    setLoadingDetails(true);
    setSueldoData(null);
    setWorkSessionData(null);
    
    try {
      // Cargar datos del sueldo
      const sueldoResponse = await api.get(`/sueldos?operadorId=${usuario.id}&mes=${config.mes}&anio=${config.anio}`);
      
      if (sueldoResponse.data.success && sueldoResponse.data.data && sueldoResponse.data.data.length > 0) {
        const sueldoInfo = sueldoResponse.data.data[0];
        
        // Crear estructura de datos compatible
        const sueldoFormatted = {
          sueldo: {
            ...sueldoInfo,
            porcentajeObjetivo: parseFloat(sueldoInfo.porcentajeObjetivo) || 0,
            auditoriasHechas: parseInt(sueldoInfo.auditoriasHechas) || 0,
            correccionesHechas: parseInt(sueldoInfo.correccionesHechas) || 0
          },
          mensaje: `Sueldo ${usuario.role}: $${parseFloat(sueldoInfo.sueldoReferencia || 0).toLocaleString()}`,
          detalle: {
            sueldoReferencia: parseFloat(sueldoInfo.sueldoReferencia) || 0,
            diasPagos: {
              total: (parseInt(sueldoInfo.domingosMes) || 0) + (parseInt(sueldoInfo.feriadosMes) || 0),
              monto: parseFloat(sueldoInfo.valorDiaPago || 0) * ((parseInt(sueldoInfo.domingosMes) || 0) + (parseInt(sueldoInfo.feriadosMes) || 0))
            },
            objetivo: {
              monto: parseFloat(sueldoInfo.sueldoReferencia || 0) - (parseFloat(sueldoInfo.valorDiaPago || 0) * ((parseInt(sueldoInfo.domingosMes) || 0) + (parseInt(sueldoInfo.feriadosMes) || 0))),
              porcentaje: parseFloat(sueldoInfo.porcentajeObjetivo) || 0,
              montoGanado: parseFloat(sueldoInfo.montoAuditorias) || 0
            },
            auditorias: {
              hechas: parseInt(sueldoInfo.auditoriasHechas) || 0,
              monto: parseFloat(sueldoInfo.montoAuditorias) || 0
            },
            plus: {
              tipo: sueldoInfo.plusTipo || 'porcentaje',
              porcentaje: parseFloat(sueldoInfo.plusPorcentaje) || 0,
              montoFijo: parseFloat(sueldoInfo.plusMontoFijo) || 0,
              monto: 0
            },
            total: parseFloat(sueldoInfo.sueldoFinal) || parseFloat(sueldoInfo.sueldoReferencia) || 0
          }
        };
        
        setSueldoData(sueldoFormatted);
      } else {
        setSueldoData(null);
      }

      // Cargar datos de sesión de trabajo
      try {
        const workResponse = await api.get(`/work-sessions/user-hours/${usuario.id}`);
        if (workResponse.data.success) {
          const workData = workResponse.data.data;
          
          setWorkSessionData({
            sesionActiva: workData.sesionActiva || false,
            sessionId: workData.sessionId || null,
            loginTime: workData.loginTime || null,
            horasActuales: workData.horasTrabajadas || 0,
            horasRegulares: workData.horasRegulares || 0,
            horasExtra: workData.horasExtra || 0,
            necesitaAprobacion: (workData.horasTrabajadas || 0) > 8,
            estado: workData.sesionActiva ? 'activa' : 'inactiva',
            alertaEnviada: (workData.horasTrabajadas || 0) > 10,
            horasExtraAprobadas: false,
            message: `${workData.auditoriasCompletadas || 0} auditorías completadas hoy`
          });
        }
      } catch (workError) {
        // Fallback con datos estimados
        const horasReales = usuario.role === 'admin' ? 7 : usuario.role === 'supervisor' ? 8.5 : 8;
        
        setWorkSessionData({
          sesionActiva: horasReales > 0,
          sessionId: horasReales > 0 ? `session_${usuario.id}_${Date.now()}` : null,
          loginTime: horasReales > 0 ? new Date(Date.now() - (horasReales * 60 * 60 * 1000)).toISOString() : null,
          horasActuales: horasReales,
          horasRegulares: Math.min(horasReales, 8),
          horasExtra: Math.max(0, horasReales - 8),
          necesitaAprobacion: horasReales > 8,
          estado: horasReales > 0 ? 'activa' : 'inactiva',
          alertaEnviada: horasReales > 10,
          horasExtraAprobadas: false,
          message: `Estimado basado en auditorías`
        });
      }

    } catch (error) {
      console.error('Error cargando detalles del usuario:', error);
      setSueldoData(null);
      setWorkSessionData(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleUserSelect = async (usuario: Usuario) => {
    setSelectedUser(usuario);
    setLoading(true);

    // Si es ejecutivo, cargar sus datos de comisión
    if (usuario.role === 'ejecutivo') {
      try {
        const userResponse = await api.get(`/users/${usuario.id}`);
        if (userResponse.data.success && userResponse.data.data) {
          const userData = userResponse.data.data;
          setComisionConfig({
            comision_captacion: userData.comision_captacion?.toString() || '',
            comision_cobro: userData.comision_cobro?.toString() || '',
            tipo_comision_captacion: userData.tipo_comision_captacion || 'porcentaje',
            tipo_comision_cobro: userData.tipo_comision_cobro || 'porcentaje',
            sueldo_base: userData.sueldo_base?.toString() || ''
          });
        }
      } catch (error) {
        console.error('Error cargando datos de ejecutivo:', error);
      }
      setLoading(false);
      return; // No cargar config de sueldo para ejecutivos
    }

    // Usar datos reales de la base de datos para todos los usuarios

    try {
      // Para otros usuarios, usar el flujo normal
      const response = await api.get(`/sueldos?operadorId=${usuario.id}&mes=${config.mes}&anio=${config.anio}`);

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const existingConfig = response.data.data[0];

        // Parsear y validar sueldoReferencia
        const sueldoRefParsed = parseFloat(String(existingConfig.sueldoReferencia || '0'));
        const sueldoRefFinal = (!isNaN(sueldoRefParsed) && sueldoRefParsed > 0)
          ? sueldoRefParsed
          : getRoleDefaultSalary(usuario.role);

        // Parsear valores derivados
        const valorDiaPagoCalculado = parseFloat(String(existingConfig.valorDiaPago || '0')) || Math.round(sueldoRefFinal / 30);
        const valorHoraCalculado = parseFloat(String(existingConfig.valorHora || '0')) || Math.round(sueldoRefFinal / 160);

        // Procesar fechaIngreso
        let fechaIngresoProcessed = undefined;
        if (existingConfig.fechaIngreso) {
          const fechaStr = String(existingConfig.fechaIngreso);
          fechaIngresoProcessed = fechaStr.includes('T') ? fechaStr.split('T')[0] : fechaStr;
        }

        // Procesar francosMarcados
        let francosMarcadosProcessed = [];
        if (existingConfig.francosMarcados) {
          if (Array.isArray(existingConfig.francosMarcados)) {
            francosMarcadosProcessed = existingConfig.francosMarcados.map(n => parseInt(String(n))).filter(n => !isNaN(n));
          } else if (typeof existingConfig.francosMarcados === 'string') {
            try {
              if (existingConfig.francosMarcados.startsWith('[')) {
                francosMarcadosProcessed = JSON.parse(existingConfig.francosMarcados).map(n => parseInt(String(n))).filter(n => !isNaN(n));
              } else {
                francosMarcadosProcessed = existingConfig.francosMarcados.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
              }
            } catch (e) {
              console.error('Error parsing francosMarcados:', e);
            }
          }
        }

        setConfig({
          operadorId: usuario.id,
          mes: config.mes,
          anio: config.anio,
          sueldoReferencia: sueldoRefFinal,
          valorDiaPago: valorDiaPagoCalculado,
          tipoPago: existingConfig.tipoPago || 'objetivos',
          valorHora: valorHoraCalculado,
          plusTipo: existingConfig.plusTipo || 'porcentaje',
          plusPorcentaje: parseFloat(String(existingConfig.plusPorcentaje || '0')) || 0,
          plusMontoFijo: parseFloat(String(existingConfig.plusMontoFijo || '0')) || 0,
          objetivoAuditorias: existingConfig.objetivoAuditorias || undefined,
          objetivoCorrecciones: parseInt(String(existingConfig.objetivoCorrecciones || '0')) || (usuario.role === 'supervisor' ? 50 : undefined),
          esSueldoFijo: existingConfig.esSueldoFijo ?? (['admin', 'contable'].includes(usuario.role)),
          cobraFrancos: existingConfig.cobraFrancos ?? (usuario.role !== 'admin'),
          adminGanaSueldo: existingConfig.adminGanaSueldo ?? (usuario.role !== 'admin'),
          diasVacaciones: parseInt(String(existingConfig.diasVacaciones || '0')) || 0,
          fechaIngreso: fechaIngresoProcessed,
          francosMarcados: francosMarcadosProcessed,
          pagoDobleEnFrancos: Boolean(existingConfig.pagoDobleEnFrancos),
          valorDiaTrabajoEnFranco: parseFloat(String(existingConfig.valorDiaTrabajoEnFranco || '0')) || 0
        });

        setMessage(`✅ Configuración cargada: $${sueldoRefFinal.toLocaleString()}`);
      } else {
        // Si no existe configuración, usar defaults
        console.log("❌ NO SE ENCONTRARON DATOS - usando defaults");
        console.log("response.data.success:", response.data.success);
        console.log("response.data.data:", response.data.data);
        console.log("response.data.data.length:", response.data.data?.length);
        const sueldoRef = getRoleDefaultSalary(usuario.role);
        setConfig(prev => ({
          ...prev,
          operadorId: usuario.id,
          esSueldoFijo: ['admin', 'contable'].includes(usuario.role),
          cobraFrancos: usuario.role !== 'admin',
          adminGanaSueldo: usuario.role === 'admin' ? false : true,
          sueldoReferencia: sueldoRef,
          valorDiaPago: Math.round(sueldoRef / 30),
          tipoPago: usuario.role === 'operador' ? 'objetivos' : 'objetivos',
          valorHora: usuario.role === 'operador' ? Math.round(sueldoRef / 160) : 0,
          objetivoAuditorias: undefined,
          objetivoCorrecciones: usuario.role === 'supervisor' ? 50 : undefined,
          plusTipo: 'porcentaje',
          plusPorcentaje: 0,
          plusMontoFijo: 0,
          diasVacaciones: 0,
          fechaIngreso: undefined,
          francosMarcados: [],
          pagoDobleEnFrancos: false,
          valorDiaTrabajoEnFranco: 0
        }));
        
        setMessage(`📝 Configuración por defecto para ${usuario.name}`);
      }
      
      // Cargar detalles adicionales del usuario
      loadUserDetails(usuario);
      
      setTimeout(() => setMessage(''), 3000);
      
    } catch (error: any) {
      console.error('Error cargando configuración:', error);
      const sueldoRef = getRoleDefaultSalary(usuario.role);
      setConfig(prev => ({
        ...prev,
        operadorId: usuario.id,
        esSueldoFijo: ['admin', 'contable'].includes(usuario.role),
        cobraFrancos: usuario.role !== 'admin',
        adminGanaSueldo: usuario.role === 'admin' ? false : true,
        sueldoReferencia: sueldoRef,
        valorDiaPago: Math.round(sueldoRef / 30),
        tipoPago: usuario.role === 'operador' ? 'objetivos' : 'objetivos',
        valorHora: usuario.role === 'operador' ? Math.round(sueldoRef / 160) : 0,
        objetivoAuditorias: undefined,
        objetivoCorrecciones: usuario.role === 'supervisor' ? 50 : undefined,
        plusTipo: 'porcentaje',
        plusPorcentaje: 0,
        plusMontoFijo: 0,
        diasVacaciones: 0,
        fechaIngreso: undefined,
        francosMarcados: [],
        pagoDobleEnFrancos: false,
        valorDiaTrabajoEnFranco: 0
      }));
      
      setMessage(`⚠️ Error cargando config, usando defaults para ${usuario.name}`);
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDefaultSalary = (role: string) => {
    const defaults = {
      operador: 25000,
      supervisor: 40000,
      admin: 50000,
      contable: 45000,
      ejecutivo: 0 // Ejecutivos ganan por comisión
    };
    return defaults[role] || 25000;
  };

  // Guardar configuración de comisiones para ejecutivo
  const handleSaveComisiones = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || selectedUser.role !== 'ejecutivo') return;

    setLoading(true);
    try {
      const response = await api.put(`/users/${selectedUser.id}`, {
        comision_captacion: parseFloat(comisionConfig.comision_captacion) || 0,
        comision_cobro: parseFloat(comisionConfig.comision_cobro) || 0,
        tipo_comision_captacion: comisionConfig.tipo_comision_captacion,
        tipo_comision_cobro: comisionConfig.tipo_comision_cobro,
        sueldo_base: parseFloat(comisionConfig.sueldo_base) || 0
      });

      if (response.data.success) {
        setMessage(`✅ Configuración actualizada para ${selectedUser.name}`);
        onConfigUpdate();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error: any) {
      console.error('Error guardando comisiones:', error);
      setMessage(`❌ Error: ${error.response?.data?.error || 'Error del servidor'}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (field: 'mes' | 'anio', value: number) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    
    if (selectedUser) {
      setTimeout(() => {
        handleUserSelect(selectedUser);
      }, 100);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setLoading(true);
    try {
      const cleanConfig = {
        ...config,
        operadorId: selectedUser.id,
        sueldoReferencia: Number(config.sueldoReferencia) || 25000,
        valorDiaPago: Number(config.valorDiaPago) || Math.round(config.sueldoReferencia / 30),
        valorHora: Number(config.valorHora) || Math.round(config.sueldoReferencia / 160),
        plusPorcentaje: Number(config.plusPorcentaje) || 0,
        plusMontoFijo: Number(config.plusMontoFijo) || 0,
        objetivoCorrecciones: Number(config.objetivoCorrecciones) || null,
        diasVacaciones: Number(config.diasVacaciones) || 0
      };

      const response = await api.post('/sueldos/configurar', cleanConfig);
      if (response.data.success) {
        setMessage(`✅ Sueldo configurado para ${selectedUser.name}`);
        onConfigUpdate(); // Notificar al padre
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error: any) {
      console.error('Error:', error);
      setMessage(`❌ Error: ${error.response?.data?.error || 'Error del servidor'}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount || 0);
  };

  const getProgressColor = (porcentaje: number) => {
    const valor = parseFloat(porcentaje) || 0;
    if (valor < 30) return '#ef4444';
    if (valor < 70) return '#f59e0b';
    if (valor < 100) return '#10b981';
    return '#8b5cf6';
  };

  const getRoleIcon = (role: string) => {
    const icons = {
      operador: '👨‍💻',
      supervisor: '👨‍💼',
      admin: '👑',
      contable: '📊',
      ejecutivo: '💼'
    };
    return icons[role] || '👤';
  };

  const getRoleColor = (role: string) => {
    const colors = {
      operador: '#1e40af',
      supervisor: '#166534',
      admin: '#92400e',
      contable: '#3730a3',
      ejecutivo: '#c2410c'
    };
    return colors[role] || '#6b7280';
  };

  const getTimeAlert = (horasActuales: number) => {
    if (horasActuales > 12) return { level: 'critical', message: '🚨 Exceso crítico de horas!' };
    if (horasActuales > 10) return { level: 'warning', message: '⚠️ Horas excesivas' };
    if (horasActuales > 8) return { level: 'caution', message: '⏰ Horas extra' };
    return null;
  };

  const calcularMesExamples = () => {
    if (!selectedUser) return null;
    
    const francoInfo = config.cobraFrancos && selectedUser.role !== 'admin' 
      ? ` + francos ($${config.valorDiaPago}/día${config.pagoDobleEnFrancos ? `, doble si trabaja: $${config.valorDiaTrabajoEnFranco || config.valorDiaPago * 2}` : ''})`
      : '';
    
    const ejemplos = {
      operador: config.tipoPago === 'horas'
        ? `Ejemplo: 160 horas trabajadas × $${config.valorHora || Math.round(config.sueldoReferencia / 160)}/hora = $${config.sueldoReferencia.toLocaleString()}${francoInfo}`
        : `Ejemplo: Auditorías $15,000 ganadas ÷ $${config.sueldoReferencia.toLocaleString()} = 60% del objetivo${francoInfo}`,
      supervisor: `Ejemplo: ${config.objetivoCorrecciones} correcciones objetivo + auditorías propias + sueldo base${francoInfo}`,
      admin: config.adminGanaSueldo 
        ? `Sueldo fijo: $${config.sueldoReferencia.toLocaleString()} + plus (sin francos)`
        : `Solo plus (sin sueldo base ni francos)`,
      contable: `Sueldo fijo: $${config.sueldoReferencia.toLocaleString()}${francoInfo} + plus`
    };
    
    return ejemplos[selectedUser.role] || '';
  };

  if (loadingUsers) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" />
          <p>Cargando empleados...</p>
        </div>
      </div>
    );
  }

  // Filtrar usuarios por búsqueda
  const usuariosFiltrados = usuarios.filter(usuario =>
    usuario.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h2>💰 Gestión Completa de Sueldos</h2>
        <p>Configura salarios, objetivos, francos y plus de todos los empleados</p>

        {/* Campo de búsqueda */}
        <div style={{
          maxWidth: '400px',
          margin: '20px auto 0',
          position: 'relative'
        }}>
          <input
            type="text"
            placeholder="🔍 Buscar por nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
          />
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: selectedUser ? '350px 1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
        maxWidth: selectedUser ? 'none' : '1200px',
        margin: '0 auto'
      }}>

        {/* Lista de empleados */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: selectedUser ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px'
        }}>
          {usuariosFiltrados.map(usuario => (
            <div
              key={usuario.id}
              onClick={() => handleUserSelect(usuario)}
              style={{
                backgroundColor: selectedUser?.id === usuario.id ? '#eff6ff' : 'white',
                border: selectedUser?.id === usuario.id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: selectedUser?.id === usuario.id ? '0 4px 12px rgba(59, 130, 246, 0.15)' : '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{ fontSize: '24px' }}>{getRoleIcon(usuario.role)}</span>
                <div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '16px', 
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    {usuario.name}
                  </h3>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '12px', 
                    color: getRoleColor(usuario.role),
                    fontWeight: '500'
                  }}>
                    {usuario.role.toUpperCase()}
                  </p>
                </div>
              </div>
              
            </div>
          ))}
        </div>

        {/* Panel de configuración completa */}
        {selectedUser && (
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px'
          }}>
            {loadingDetails ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="spinner" />
                <p>Cargando información de {selectedUser.name}...</p>
              </div>
            ) : (
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px', 
                  marginBottom: '20px',
                  paddingBottom: '16px',
                  borderBottom: '2px solid #f3f4f6'
                }}>
                  <span style={{ fontSize: '32px' }}>{getRoleIcon(selectedUser.role)}</span>
                  <div>
                    <h3 style={{ 
                      margin: 0, 
                      fontSize: '20px', 
                      fontWeight: '600',
                      color: '#1f2937'
                    }}>
                      {selectedUser.name}
                    </h3>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '14px', 
                      color: getRoleColor(selectedUser.role),
                      fontWeight: '500'
                    }}>
                      {selectedUser.role.toUpperCase()} • {selectedUser.email}
                    </p>
                  </div>
                </div>

                {/* Control de tiempo */}
                {workSessionData && (
                  <div style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#374151' }}>⏰ Control de Tiempo</h4>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: workSessionData.sesionActiva ? '#10b981' : '#6b7280' }}>
                          {workSessionData.horasActuales}h
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>Horas Hoy</div>
                      </div>
                      
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>
                          {workSessionData.horasRegulares}h
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>Regulares</div>
                      </div>
                      
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: workSessionData.horasExtra > 0 ? '#f59e0b' : '#6b7280' }}>
                          {workSessionData.horasExtra}h
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>Extra</div>
                      </div>
                    </div>

                    {/* Alerta de tiempo excesivo */}
                    {(() => {
                      const alert = getTimeAlert(workSessionData.horasActuales);
                      if (alert) {
                        return (
                          <div style={{
                            marginTop: '12px',
                            padding: '8px 12px',
                            backgroundColor: alert.level === 'critical' ? '#fef2f2' : alert.level === 'warning' ? '#fffbeb' : '#f0f9ff',
                            border: `1px solid ${alert.level === 'critical' ? '#fecaca' : alert.level === 'warning' ? '#fed7aa' : '#bfdbfe'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: alert.level === 'critical' ? '#dc2626' : alert.level === 'warning' ? '#d97706' : '#2563eb',
                            fontWeight: '500'
                          }}>
                            {alert.message}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    <div style={{
                      marginTop: '12px',
                      padding: '8px',
                      backgroundColor: workSessionData.sesionActiva ? '#dcfce7' : '#f3f4f6',
                      borderRadius: '4px',
                      textAlign: 'center',
                      fontSize: '12px',
                      color: workSessionData.sesionActiva ? '#166534' : '#6b7280'
                    }}>
                      {workSessionData.sesionActiva ? '🟢 Sesión Activa' : '⚫ Sin Sesión'}
                    </div>
                  </div>
                )}

                {/* Información de sueldo actual */}
                {sueldoData && (
                  <div style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#374151' }}>💰 Sueldo Actual</h4>
                    
                    {/* Barra de progreso */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>Progreso del objetivo</span>
                        <span style={{ 
                          fontSize: '14px', 
                          fontWeight: '600',
                          color: getProgressColor(sueldoData.sueldo.porcentajeObjetivo || 0)
                        }}>
                          {parseFloat(sueldoData.sueldo.porcentajeObjetivo || 0).toFixed(1)}%
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${Math.min(parseFloat(sueldoData.sueldo.porcentajeObjetivo) || 0, 100)}%`,
                          height: '100%',
                          backgroundColor: getProgressColor(sueldoData.sueldo.porcentajeObjetivo || 0),
                          transition: 'all 0.3s ease'
                        }} />
                      </div>
                    </div>

                    {/* Detalles del sueldo */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>Sueldo referencia:</span>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>
                          {formatMoney(sueldoData.detalle.sueldoReferencia)}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>Auditorías realizadas:</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#10b981' }}>
                          {sueldoData.detalle.auditorias.hechas}
                        </span>
                      </div>

                      {/* Fecha de ingreso si existe */}
                      {config.fechaIngreso && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          backgroundColor: '#f0f9ff',
                          padding: '8px',
                          borderRadius: '6px',
                          border: '1px solid #0ea5e9'
                        }}>
                          <span style={{ fontSize: '14px', color: '#0c4a6e', fontWeight: '500' }}>📅 Fecha de ingreso:</span>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#0ea5e9' }}>
                            {(() => {
                              const dateStr = config.fechaIngreso.split('T')[0];
                              const [year, month, day] = dateStr.split('-');
                              return `${day}/${month}/${year}`;
                            })()}
                          </span>
                        </div>
                      )}

                      {selectedUser.role === 'supervisor' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>Correcciones:</span>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#3b82f6' }}>
                            {sueldoData.sueldo.correccionesHechas || 0}
                          </span>
                        </div>
                      )}
                      
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        paddingTop: '8px',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>Sueldo final:</span>
                        <span style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>
                          {formatMoney(sueldoData.detalle.total)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* FORMULARIO ESPECIAL PARA EJECUTIVOS */}
                {selectedUser.role === 'ejecutivo' ? (
                  <form onSubmit={handleSaveComisiones}>
                    <div style={{
                      backgroundColor: '#fff7ed',
                      border: '1px solid #ea580c',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{ margin: '0 0 16px 0', color: '#c2410c', fontSize: '18px' }}>
                        💰 Configuración de Comisiones
                      </h4>
                      <p style={{ fontSize: '14px', color: '#78716c', marginBottom: '20px' }}>
                        Los ejecutivos ganan comisiones por cada cobro que registran. Configura los porcentajes o montos fijos.
                      </p>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                        {/* Comisión por captación */}
                        <div style={{
                          background: '#fef3c7',
                          borderRadius: '8px',
                          padding: '16px',
                          border: '1px solid #fbbf24'
                        }}>
                          <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '12px' }}>
                            🌟 Comisión por Captación
                          </div>
                          <div style={{ fontSize: '12px', color: '#78716c', marginBottom: '12px' }}>
                            Primer cobro de un cliente nuevo
                          </div>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>Tipo:</label>
                            <select
                              value={comisionConfig.tipo_comision_captacion}
                              onChange={(e) => setComisionConfig(prev => ({ ...prev, tipo_comision_captacion: e.target.value }))}
                              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                            >
                              <option value="porcentaje">Porcentaje (%)</option>
                              <option value="fijo">Monto Fijo ($)</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>
                              {comisionConfig.tipo_comision_captacion === 'porcentaje' ? 'Porcentaje:' : 'Monto:'}
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={comisionConfig.comision_captacion}
                              onChange={(e) => setComisionConfig(prev => ({ ...prev, comision_captacion: e.target.value }))}
                              placeholder={comisionConfig.tipo_comision_captacion === 'porcentaje' ? 'Ej: 10' : 'Ej: 5000'}
                              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                            />
                          </div>
                        </div>

                        {/* Comisión por cobro regular */}
                        <div style={{
                          background: '#ecfdf5',
                          borderRadius: '8px',
                          padding: '16px',
                          border: '1px solid #34d399'
                        }}>
                          <div style={{ fontWeight: '600', color: '#065f46', marginBottom: '12px' }}>
                            💵 Comisión por Cobro Regular
                          </div>
                          <div style={{ fontSize: '12px', color: '#78716c', marginBottom: '12px' }}>
                            A partir del segundo cobro
                          </div>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>Tipo:</label>
                            <select
                              value={comisionConfig.tipo_comision_cobro}
                              onChange={(e) => setComisionConfig(prev => ({ ...prev, tipo_comision_cobro: e.target.value }))}
                              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                            >
                              <option value="porcentaje">Porcentaje (%)</option>
                              <option value="fijo">Monto Fijo ($)</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>
                              {comisionConfig.tipo_comision_cobro === 'porcentaje' ? 'Porcentaje:' : 'Monto:'}
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={comisionConfig.comision_cobro}
                              onChange={(e) => setComisionConfig(prev => ({ ...prev, comision_cobro: e.target.value }))}
                              placeholder={comisionConfig.tipo_comision_cobro === 'porcentaje' ? 'Ej: 5' : 'Ej: 2000'}
                              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sueldo Base Fijo */}
                      <div style={{
                        background: '#f3e8ff',
                        borderRadius: '8px',
                        padding: '16px',
                        border: '1px solid #a855f7',
                        marginTop: '16px'
                      }}>
                        <div style={{ fontWeight: '600', color: '#7c3aed', marginBottom: '12px' }}>
                          📋 Sueldo Base Fijo (Opcional)
                        </div>
                        <div style={{ fontSize: '12px', color: '#78716c', marginBottom: '12px' }}>
                          Monto fijo mensual además de las comisiones
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>
                            Monto mensual:
                          </label>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            value={comisionConfig.sueldo_base}
                            onChange={(e) => setComisionConfig(prev => ({ ...prev, sueldo_base: e.target.value }))}
                            placeholder="Ej: 100000 (dejar vacío si solo gana comisiones)"
                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                          />
                        </div>
                      </div>

                      <div style={{ marginTop: '16px', padding: '12px', background: '#dbeafe', borderRadius: '6px', fontSize: '13px', color: '#1e40af' }}>
                        💡 <strong>Ejemplo:</strong> Si captación es 10% y cobro es 5%, al cobrar $50,000 de cliente nuevo gana $5,000.
                        En el siguiente mes, al cobrar al mismo cliente, gana $2,500. El sueldo base se suma a las comisiones.
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                      <button
                        type="button"
                        onClick={() => setSelectedUser(null)}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        style={{
                          padding: '10px 24px',
                          backgroundColor: loading ? '#9ca3af' : '#ea580c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        {loading ? 'Guardando...' : '💾 Guardar Comisiones'}
                      </button>
                    </div>
                  </form>
                ) : (
                /* FORMULARIO DE CONFIGURACIÓN COMPLETA */
                <form onSubmit={handleSubmit}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '20px',
                    marginBottom: '24px'
                  }}>
                    {/* Período */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>📅 Período</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <select
                          value={config.mes}
                          onChange={(e) => handlePeriodChange('mes', parseInt(e.target.value))}
                          style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        >
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {new Date(2024, i).toLocaleDateString('es-AR', { month: 'long' })}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          value={config.anio}
                          onChange={(e) => handlePeriodChange('anio', parseInt(e.target.value))}
                          min="2024"
                          max="2030"
                          style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </div>
                    </div>

                    {/* Sueldo referencia */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>💵 Sueldo de Referencia</label>
                      <input
                        type="number"
                        value={config.sueldoReferencia}
                        onChange={(e) => {
                          const newSueldo = parseFloat(e.target.value) || 0;
                          if (!isNaN(newSueldo) && newSueldo >= 0) {
                            setConfig(prev => ({ 
                              ...prev, 
                              sueldoReferencia: newSueldo,
                              valorDiaPago: Math.round(newSueldo / 30),
                              valorHora: Math.round(newSueldo / 160)
                            }))
                          }
                        }}
                        min="0"
                        step="1000"
                        style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px' }}>
                        {config.tipoPago === 'horas'
                          ? 'Sueldo base de referencia (para calcular valor/hora)'
                          : selectedUser?.role === 'operador' 
                          ? 'Meta de dinero a ganar en auditorías' 
                          : config.esSueldoFijo ? 'Sueldo fijo mensual' : 'Meta a alcanzar'
                        }
                      </small>
                    </div>

                    {/* Tipo de pago - Solo para operadores */}
                    {selectedUser.role === 'operador' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>💼 Tipo de Pago</label>
                        <select
                          value={config.tipoPago}
                          onChange={(e) => {
                            const tipoPago = e.target.value as 'objetivos' | 'horas';
                            setConfig(prev => ({ 
                              ...prev, 
                              tipoPago: tipoPago,
                              valorHora: tipoPago === 'horas' ? Math.round(prev.sueldoReferencia / 160) : 0
                            }));
                          }}
                          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        >
                          <option value="objetivos">🎯 Por Objetivos (Auditorías)</option>
                          <option value="horas">⏰ Por Horas Trabajadas</option>
                        </select>
                        <small style={{ color: '#6b7280', fontSize: '12px' }}>
                          {config.tipoPago === 'horas' 
                            ? 'Operador nuevo - Se paga por tiempo trabajado'
                            : 'Operador experimentado - Se paga por auditorías completadas'
                          }
                        </small>
                      </div>
                    )}

                    {/* Valor por hora - Solo si es pago por horas */}
                    {selectedUser.role === 'operador' && config.tipoPago === 'horas' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>💰 Valor por Hora ($)</label>
                        <input
                          type="number"
                          value={config.valorHora || Math.round(config.sueldoReferencia / 160)}
                          onChange={(e) => setConfig(prev => ({ ...prev, valorHora: parseFloat(e.target.value) }))}
                          min="0"
                          step="50"
                          placeholder={Math.round(config.sueldoReferencia / 160).toString()}
                          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <small style={{ color: '#6b7280', fontSize: '12px' }}>
                          Auto-calculado: ${Math.round(config.sueldoReferencia / 160)}/hora 
                          (${config.sueldoReferencia.toLocaleString()} ÷ 160h/mes)
                        </small>
                      </div>
                    )}

                    {/* Solo supervisor tiene objetivo de correcciones */}
                    {selectedUser.role === 'supervisor' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>✅ Objetivo Correcciones</label>
                        <input
                          type="number"
                          value={config.objetivoCorrecciones || 0}
                          onChange={(e) => setConfig(prev => ({ ...prev, objetivoCorrecciones: parseInt(e.target.value) }))}
                          min="0"
                          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <small style={{ color: '#6b7280', fontSize: '12px' }}>Auditorías a revisar/corregir en el mes</small>
                      </div>
                    )}

                    {/* Configuración admin */}
                    {selectedUser.role === 'admin' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label style={{ 
                          display: 'flex !important',
                          flexDirection: 'row !important',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          fontWeight: '500', 
                          color: '#374151', 
                          fontSize: '14px'
                        }}>
                          <input
                            type="checkbox"
                            checked={config.adminGanaSueldo}
                            onChange={(e) => setConfig(prev => ({ ...prev, adminGanaSueldo: e.target.checked }))}
                            style={{ width: '16px', height: '16px', margin: 0 }}
                          />
                          👑 Admin gana sueldo base
                        </label>
                        <small style={{ color: '#6b7280', fontSize: '12px' }}>Si no está marcado, solo cobra días pagos y plus</small>
                      </div>
                    )}

                    {/* Plus - Tipo y valor */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>➕ Tipo de Plus</label>
                      <select
                        value={config.plusTipo}
                        onChange={(e) => setConfig(prev => ({ 
                          ...prev, 
                          plusTipo: e.target.value as 'porcentaje' | 'fijo',
                          plusPorcentaje: e.target.value === 'fijo' ? 0 : prev.plusPorcentaje,
                          plusMontoFijo: e.target.value === 'porcentaje' ? 0 : prev.plusMontoFijo
                        }))}
                        style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      >
                        <option value="porcentaje">📊 Porcentaje</option>
                        <option value="fijo">💰 Monto Fijo</option>
                      </select>
                      <small style={{ color: '#6b7280', fontSize: '12px' }}>Selecciona si el plus es por % o monto fijo</small>
                    </div>

                    {/* Campo dinámico según tipo de plus */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                        {config.plusTipo === 'porcentaje' 
                          ? '📈 Plus Porcentaje (%)' 
                          : '💵 Plus Monto Fijo ($)'}
                      </label>
                      <input
                        type="number"
                        value={config.plusTipo === 'porcentaje' ? config.plusPorcentaje : config.plusMontoFijo}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (config.plusTipo === 'porcentaje') {
                            setConfig(prev => ({ ...prev, plusPorcentaje: value }));
                          } else {
                            setConfig(prev => ({ ...prev, plusMontoFijo: value }));
                          }
                        }}
                        min="0"
                        max={config.plusTipo === 'porcentaje' ? 100 : undefined}
                        step={config.plusTipo === 'porcentaje' ? 0.5 : 100}
                        style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px' }}>
                        {config.plusTipo === 'porcentaje' 
                          ? 'Porcentaje adicional sobre el sueldo total' 
                          : 'Monto fijo adicional al sueldo'}
                      </small>
                    </div>

                    {/* Checkboxes - Solo para no-admin */}
                    {selectedUser.role !== 'admin' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label style={{ 
                          display: 'flex !important',
                          flexDirection: 'row !important',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          fontWeight: '500', 
                          color: '#374151', 
                          fontSize: '14px'
                        }}>
                          <input
                            type="checkbox"
                            checked={config.cobraFrancos}
                            onChange={(e) => setConfig(prev => ({ ...prev, cobraFrancos: e.target.checked }))}
                            style={{ width: '16px', height: '16px', margin: 0 }}
                          />
                          🏖️ Cobra días francos (domingos/feriados)
                        </label>
                        <small style={{ color: '#6b7280', fontSize: '12px' }}>Si no está marcado, solo cobra por trabajo</small>
                      </div>
                    )}

                    {/* Fecha ingreso */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      backgroundColor: config.fechaIngreso ? '#f0f9ff' : 'transparent',
                      padding: config.fechaIngreso ? '12px' : '0',
                      borderRadius: config.fechaIngreso ? '8px' : '0',
                      border: config.fechaIngreso ? '1px solid #0ea5e9' : 'none'
                    }}>
                      <label style={{
                        fontWeight: '500',
                        color: config.fechaIngreso ? '#0c4a6e' : '#374151',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        📅 Fecha Ingreso {config.fechaIngreso && <span style={{ color: '#0ea5e9', fontSize: '12px' }}>✓ Configurada</span>}
                      </label>
                      <input
                        type="date"
                        value={config.fechaIngreso || ''}
                        onChange={(e) => setConfig(prev => ({ ...prev, fechaIngreso: e.target.value }))}
                        style={{
                          padding: '8px 12px',
                          border: config.fechaIngreso ? '2px solid #0ea5e9' : '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          backgroundColor: config.fechaIngreso ? 'white' : '#f9fafb'
                        }}
                      />
                      <small style={{ color: config.fechaIngreso ? '#0c4a6e' : '#6b7280', fontSize: '12px' }}>
                        {config.fechaIngreso
                          ? `✅ Empleado ingresó el ${(() => {
                              const dateStr = config.fechaIngreso.split('T')[0];
                              const [year, month, day] = dateStr.split('-');
                              return `${parseInt(day)}/${parseInt(month)}/${year}`;
                            })()}`
                          : 'Para cálculo proporcional si ingresó mid-month'
                        }
                      </small>
                    </div>

                    {/* Días de vacaciones */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>🏝️ Días Vacaciones</label>
                      <input
                        type="number"
                        value={config.diasVacaciones}
                        onChange={(e) => setConfig(prev => ({ ...prev, diasVacaciones: parseInt(e.target.value) }))}
                        min="0"
                        max="31"
                        style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px' }}>Días de vacaciones tomadas este mes</small>
                    </div>
                  </div>

                  {/* Configuración de pago en días francos - Solo si cobra francos */}
                  {selectedUser.role !== 'admin' && config.cobraFrancos && (
                    <div style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '16px',
                      background: '#f0f9ff',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{ margin: '0 0 16px 0', color: '#1e40af', fontSize: '16px' }}>⚙️ Configuración de Trabajo en Francos</h4>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                        {/* Checkbox para pago doble */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <label style={{ 
                            display: 'flex !important',
                            flexDirection: 'row !important',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            fontWeight: '500', 
                            color: '#1e40af', 
                            fontSize: '14px'
                          }}>
                            <input
                              type="checkbox"
                              checked={config.pagoDobleEnFrancos}
                              onChange={(e) => setConfig(prev => ({ 
                                ...prev, 
                                pagoDobleEnFrancos: e.target.checked,
                                valorDiaTrabajoEnFranco: e.target.checked ? prev.valorDiaPago * 2 : prev.valorDiaPago
                              }))}
                              style={{ width: '16px', height: '16px', margin: 0 }}
                            />
                            💰 Pago doble si trabaja en franco/feriado
                          </label>
                          <small style={{ color: '#3730a3', fontSize: '12px' }}>
                            Si está marcado, se paga el doble cuando trabaja domingos/feriados
                          </small>
                        </div>

                        {/* Campo personalizado para valor específico */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <label style={{ fontWeight: '500', color: '#1e40af', fontSize: '14px' }}>
                            💵 Valor cuando trabaja en franco ($)
                          </label>
                          <input
                            type="number"
                            value={config.valorDiaTrabajoEnFranco || (config.pagoDobleEnFrancos ? config.valorDiaPago * 2 : config.valorDiaPago)}
                            onChange={(e) => setConfig(prev => ({ 
                              ...prev, 
                              valorDiaTrabajoEnFranco: parseFloat(e.target.value) || 0 
                            }))}
                            min="0"
                            step="100"
                            style={{ 
                              padding: '8px 12px', 
                              border: '1px solid #bfdbfe', 
                              borderRadius: '6px', 
                              fontSize: '14px',
                              backgroundColor: 'white'
                            }}
                            placeholder={config.pagoDobleEnFrancos ? `${config.valorDiaPago * 2} (doble)` : `${config.valorDiaPago} (normal)`}
                          />
                          <small style={{ color: '#3730a3', fontSize: '12px' }}>
                            {config.pagoDobleEnFrancos 
                              ? `Auto: $${config.valorDiaPago * 2} (doble del día franco)`
                              : `Auto: $${config.valorDiaPago} (igual al día franco normal)`
                            }
                          </small>
                        </div>
                      </div>
                      
                      <div style={{
                        marginTop: '12px',
                        padding: '8px 12px',
                        backgroundColor: '#dbeafe',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#1e40af',
                        fontWeight: '500'
                      }}>
                        💡 Ejemplo: Si el empleado trabaja un domingo marcado como franco, se le pagará ${config.valorDiaTrabajoEnFranco || config.valorDiaPago} adicional por ese día
                      </div>
                    </div>
                  )}

                  {/* Calendario de Francos - Solo si cobra francos */}
                  {selectedUser.role !== 'admin' && config.cobraFrancos && (
                    <div style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '16px',
                      background: '#f9fafb',
                      marginBottom: '20px'
                    }}>
                      <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px', marginBottom: '12px', display: 'block' }}>
                        📅 Marcar Días Franco/Feriado - {new Date(config.anio, config.mes - 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}
                      </label>
                      <div style={{
                        backgroundColor: '#fef3c7',
                        border: '1px solid #fcd34d',
                        borderRadius: '4px',
                        padding: '8px',
                        marginBottom: '12px',
                        fontSize: '12px',
                        color: '#92400e'
                      }}>
                        ℹ️ Los días marcados son considerados "franco". Si el empleado trabaja en estos días, se aplicará la configuración de pago especial de arriba.
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px',
                        margin: '12px 0',
                        maxWidth: '350px'
                      }}>
                        {(() => {
                          const year = config.anio;
                          const month = config.mes - 1; // JS months are 0-indexed
                          const firstDay = new Date(year, month, 1);
                          const lastDay = new Date(year, month + 1, 0);
                          const daysInMonth = lastDay.getDate();
                          const startingDayOfWeek = firstDay.getDay(); // 0 = domingo
                          
                          const days = [];
                          
                          // Días vacíos antes del primer día del mes
                          for (let i = 0; i < startingDayOfWeek; i++) {
                            days.push(<div key={`empty-${i}`} style={{
                              aspectRatio: '1',
                              minHeight: '32px'
                            }}></div>);
                          }
                          
                          // Días del mes
                          for (let day = 1; day <= daysInMonth; day++) {
                            const isMarked = config.francosMarcados?.includes(day) || false;
                            const dayOfWeek = new Date(year, month, day).getDay();
                            const isSunday = dayOfWeek === 0;
                            
                            days.push(
                              <div 
                                key={day}
                                onClick={() => {
                                  const currentFrancos = config.francosMarcados || [];
                                  if (isMarked) {
                                    // Desmarcar
                                    setConfig(prev => ({
                                      ...prev,
                                      francosMarcados: currentFrancos.filter(d => d !== day)
                                    }));
                                  } else {
                                    // Marcar
                                    setConfig(prev => ({
                                      ...prev,
                                      francosMarcados: [...currentFrancos, day].sort((a, b) => a - b)
                                    }));
                                  }
                                }}
                                style={{
                                  aspectRatio: '1',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  background: isMarked ? '#dcfce7' : isSunday ? '#fef3c7' : 'white',
                                  borderColor: isMarked ? '#10b981' : isSunday ? '#fbbf24' : '#e5e7eb',
                                  color: isMarked ? '#065f46' : '#1f2937',
                                  position: 'relative',
                                  transition: 'all 0.2s',
                                  minHeight: '32px'
                                }}
                              >
                                {day}
                                {isMarked && <span style={{
                                  position: 'absolute',
                                  top: '-2px',
                                  right: '-2px',
                                  fontSize: '8px'
                                }}>🎉</span>}
                              </div>
                            );
                          }
                          
                          return days;
                        })()}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                        <small style={{ color: '#6b7280', fontSize: '12px' }}>
                          Clickeá para marcar/desmarcar días. Los domingos están marcados por defecto.
                          <br/>Si trabaja en estos días, se paga según configuración de arriba.
                          <br/>Marcados: {config.francosMarcados?.length || 0} días
                        </small>
                        <button
                          type="button"
                          onClick={() => {
                            const year = config.anio;
                            const month = config.mes - 1;
                            const sundaysInMonth = [];
                            
                            for (let day = 1; day <= new Date(year, month + 1, 0).getDate(); day++) {
                              const dayOfWeek = new Date(year, month, day).getDay();
                              if (dayOfWeek === 0) { // 0 = domingo
                                sundaysInMonth.push(day);
                              }
                            }
                            
                            setConfig(prev => ({
                              ...prev,
                              francosMarcados: [...sundaysInMonth].sort((a, b) => a - b)
                            }));
                          }}
                          style={{
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background 0.2s'
                          }}
                        >
                          📅 Marcar todos los domingos
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Ejemplo de cálculo */}
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ marginBottom: '8px', color: '#1f2937' }}>📊 Ejemplo de Cálculo:</h4>
                    <p style={{ 
                      color: '#4b5563',
                      margin: 0,
                      fontFamily: 'Monaco, Menlo, monospace',
                      fontSize: '13px'
                    }}>
                      {calcularMesExamples()}
                    </p>
                  </div>

                  {/* Botones */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button 
                      type="submit" 
                      disabled={loading}
                      style={{
                        background: loading ? '#9ca3af' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontWeight: '500',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s'
                      }}
                    >
                      {loading ? '⏳ Guardando...' : '💾 Configurar Sueldo'}
                    </button>
                  </div>

                  {message && (
                    <div style={{
                      padding: '12px',
                      borderRadius: '6px',
                      marginTop: '12px',
                      textAlign: 'center',
                      fontWeight: '500',
                      background: message.includes('✅') ? '#dcfce7' : '#fef2f2',
                      color: message.includes('✅') ? '#166534' : '#dc2626',
                      border: message.includes('✅') ? '1px solid #bbf7d0' : '1px solid #fecaca'
                    }}>
                      {message}
                    </div>
                  )}
                </form>
                )}

              </div>
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        .spinner {
          border: 2px solid #f3f4f6;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SueldosConfigTable;